/**
 *
 *  Google Login
 *  Component
 *
 *  Created by Vladimir Shishlyannikov on 26/06/2023.
 *  Copyright (c) 2023-present iWoo Ltd. All rights reserved.
 *
 */

import React, { useCallback, useEffect, useRef, useState } from "react";

// Components
import Button from "../Button/Button";
import { useGoogleOAuth } from "../../components/GoogleOAuthProvider/GoogleOAuthProvider";

import { ReactComponent as GoogleLogo } from "./google-logo.svg";

const containerHeightMap = { large: 54, medium: 40, small: 20 };

export default function GoogleLogin({
  disabled,
  onSuccess,
  onError,
  useOneTap,
  promptMomentNotification,
  type = "standard",
  theme = "outline",
  size = "large",
  text,
  shape,
  logo_alignment,
  width,
  height,
  locale,
  click_listener,
  containerProps,
  ...props
}) {
  const btnContainerRef = useRef(null);
  const { clientId, scriptLoadedSuccessfully } = useGoogleOAuth();

  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;

  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  const promptMomentNotificationRef = useRef(promptMomentNotification);
  promptMomentNotificationRef.current = promptMomentNotification;

  const [googleLoginWrapperButton, setGoogleLoginWrapperButton] = useState();

  useEffect(() => {
    if (!scriptLoadedSuccessfully) return;

    window?.google?.accounts?.id?.initialize({
      client_id: clientId,
      callback: (credentialResponse) => {
        if (!credentialResponse.credential) {
          return onErrorRef.current?.();
        }

        const { credential, select_by } = credentialResponse;
        onSuccessRef.current({
          credential,
          clientId: extractClientId(credentialResponse),
          select_by,
        });
      },
      ...props,
    });

    window?.google?.accounts?.id?.renderButton(btnContainerRef.current, {
      type,
      theme,
      size,
      text,
      shape,
      logo_alignment,
      width: btnContainerRef.current.offsetWidth,
      height,
      locale,
      click_listener,
    });

    setGoogleLoginWrapperButton(
      btnContainerRef.current.querySelector("div[role=button]")
    );

    if (useOneTap) {
      window?.google?.accounts?.id?.prompt(promptMomentNotificationRef.current);
    }

    return () => {
      if (useOneTap) {
        window?.google?.accounts?.id?.cancel();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    clientId,
    scriptLoadedSuccessfully,
    useOneTap,
    type,
    theme,
    size,
    text,
    shape,
    logo_alignment,
    width,
    height,
    locale,
  ]);

  const handleGoogleLogin = useCallback(() => {
    // window.google.accounts.id.signIn({
    //   prompt: 'select_account', // Show account selection
    //   callback: (credentialResponse) => {
    //     if (credentialResponse.credential) {
    //       // Handle successful sign-in
    //       console.log('Signed in successfully:', credentialResponse);
    //       onSuccessRef.current({
    //         credential: credentialResponse.credential,
    //         clientId: extractClientId(credentialResponse),
    //         select_by: credentialResponse.select_by,
    //       });
    //     } else {
    //       console.error('Sign-in failed');
    //       onErrorRef.current?.();
    //     }
    //   },
    // });

    const googleLoginWrapperButton =
      btnContainerRef.current.querySelector("div[role=button]");
    if (googleLoginWrapperButton) {
      googleLoginWrapperButton.click();
    } else {
      window.google.accounts.id.prompt(); // Show the One Tap prompt Safari fix
    }
    // const googleLoginWrapperButton =
    //   btnContainerRef.current; //.querySelector("div[role=button]");
    // // googleLoginWrapperButton && googleLoginWrapperButton.click();

    // if (googleLoginWrapperButton) {
    //   // Create a new mouse event
    //   const event = new MouseEvent('click', {
    //     bubbles: true,
    //     cancelable: true,
    //     view: window
    //   });

    //   // Dispatch the event
    //   googleLoginWrapperButton.dispatchEvent(event);
    // } else {
    //   console.warn("Google login button not found");
    // }
  }, []);

  return (
    <>
      <div
        {...containerProps}
        ref={btnContainerRef}
        role="button"
        style={{
          display: "none",
          height: containerHeightMap[size],
          ...containerProps?.style,
        }}
      />
      <button
        onClick={handleGoogleLogin}
        disabled={disabled}
        className={`tbk-text-main tbk-flex tbk-w-full tbk-justify-center tbk-rounded-lg tbk-border tbk-border-solid tbk-border-grey-inactive tbk-bg-basic-white tbk-py-1.5 tbk-px-2 tbk-text-blue-dark hover:tbk-border-blue-grey-light hover:tbk-bg-blue-grey-light hover:tbk-text-blue-dark focus:tbk-border-blue-grey-light focus:tbk-bg-basic-white focus:tbk-text-blue-dark active:tbk-border-blue-grey-light active:tbk-bg-basic-white active:tbk-text-blue-dark disabled:tbk-border-grey-light disabled:tbk-bg-grey-light disabled:tbk-text-blue-grey${
          disabled ? " disabled" : ""
        }`}
      >
        <GoogleLogo />
        {size !== "small" ? <div className="tbk-ml-1.5">Google</div> : ""}
      </button>
    </>
  );
}

export function extractClientId(credentialResponse) {
  const clientId =
    credentialResponse?.clientId ?? credentialResponse?.client_id;
  return clientId;
}
